import { Outlet, createRootRoute, createRoute, createRouter, redirect } from "@tanstack/react-router";
import { CartScreen } from "./dispensary/CartScreen";
import { CheckoutScreen } from "./dispensary/CheckoutScreen";
import { OrderTrackingScreen } from "./dispensary/OrderTrackingScreen";
import { DispensaryAgeLayout } from "./dispensary/DispensaryAgeLayout";
import { MenuScreen } from "./dispensary/MenuScreen";
import { useCartStore } from "./store/cart-store";
import { AccountAddressesScreen } from "./account/AccountAddressesScreen";
import { AccountOrdersScreen } from "./account/AccountOrdersScreen";
import { AccountIdentityScreen } from "./account/AccountIdentityScreen";
import { AccountPaymentScreen } from "./account/AccountPaymentScreen";
import { AccountSubNav } from "./account/AccountSubNav";
import { AccountSupportScreen } from "./account/AccountSupportScreen";
import { CustomerShell } from "./shell/CustomerShell";
import { MarketplaceScreen } from "./pages/MarketplaceScreen";
import { SignInScreen } from "./pages/SignInScreen";
import { SignUpScreen } from "./pages/SignUpScreen";
import { AccountLandingScreen } from "./account/AccountLandingScreen";

const rootRoute = createRootRoute({
  component: CustomerShell,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  validateSearch: (raw: Record<string, unknown>): { q?: string; cat?: string; disp?: string } => {
    const out: { q?: string; cat?: string; disp?: string } = {};
    if (typeof raw.q === "string" && raw.q.trim().length > 0) out.q = raw.q.trim();
    if (typeof raw.cat === "string" && raw.cat.trim().length > 0) out.cat = raw.cat.trim();
    if (typeof raw.disp === "string" && raw.disp.trim().length > 0) out.disp = raw.disp.trim();
    return out;
  },
  component: MarketplaceScreen,
});

/**
 * Dedicated `/sign-in` route + `$` splat child for Clerk's internal
 * sub-paths (e.g., `/sign-in/factor-one`). The shell `<AuthHeader>` Sign in
 * CTA links here; in-flow auth requests across checkout / account /
 * tracking keep using `<SignInButton mode="modal">` to preserve task state.
 *
 * `redirect_url` round-trips via the URL query string — `<AuthHeader>`
 * appends it from the current pathname, Clerk's `<SignIn>` reads it
 * natively. `validateSearch` accepts the param so TanStack's typed Link
 * doesn't drop it.
 */
const signInSearchValidator = (raw: Record<string, unknown>): { redirect_url?: string } =>
  typeof raw.redirect_url === "string" && raw.redirect_url.length > 0
    ? { redirect_url: raw.redirect_url }
    : {};

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "sign-in",
  validateSearch: signInSearchValidator,
  component: SignInScreen,
});
const signInSplatRoute = createRoute({
  getParentRoute: () => signInRoute,
  path: "$",
  component: SignInScreen,
});
const signInTree = signInRoute.addChildren([signInSplatRoute]);

const signUpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "sign-up",
  validateSearch: signInSearchValidator,
  component: SignUpScreen,
});
const signUpSplatRoute = createRoute({
  getParentRoute: () => signUpRoute,
  path: "$",
  component: SignUpScreen,
});
const signUpTree = signUpRoute.addChildren([signUpSplatRoute]);

const ordersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "orders",
  component: function OrdersPage() {
    return (
      <>
        <AccountSubNav />
        <AccountOrdersScreen />
      </>
    );
  },
});

const dispensaryShellRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "$dispensarySlug",
  beforeLoad: ({ params }) => {
    if (params.dispensarySlug === "account") {
      throw redirect({ to: "/account" });
    }
  },
  component: function DispensaryShell() {
    const { dispensarySlug } = dispensaryShellRoute.useParams();
    return <DispensaryAgeLayout dispensarySlug={dispensarySlug} />;
  },
});

const dispensaryMenuRoute = createRoute({
  getParentRoute: () => dispensaryShellRoute,
  path: "menu",
  component: function MenuRoute() {
    const { dispensarySlug } = dispensaryMenuRoute.useParams();
    return <MenuScreen dispensarySlug={dispensarySlug} />;
  },
});

const dispensaryCartRoute = createRoute({
  getParentRoute: () => dispensaryShellRoute,
  path: "cart",
  component: function CartRoute() {
    const { dispensarySlug } = dispensaryCartRoute.useParams();
    return <CartScreen dispensarySlug={dispensarySlug} />;
  },
});

const checkoutLayoutRoute = createRoute({
  getParentRoute: () => dispensaryShellRoute,
  path: "checkout",
  /**
   * Cart-empty guard (redline 05 flag 8): the new single-page checkout's
   * always-visible summary makes "Place order" nonsensical with no items.
   * Bounce to the shop's menu (not /cart, which would also be empty and is
   * a redundant hop). Relies on cart-store's Zustand `persist` middleware
   * being a synchronous localStorage hydrate at store init — if cart sync
   * ever becomes async, this guard needs to wait on a `cartReady` signal
   * before reading.
   */
  beforeLoad: ({ params }) => {
    const lines = useCartStore.getState().carts[params.dispensarySlug] ?? [];
    if (lines.length === 0) {
      throw redirect({
        to: "/$dispensarySlug/menu",
        params: { dispensarySlug: params.dispensarySlug },
      });
    }
  },
  component: function CheckoutLayoutRoute() {
    return <Outlet />;
  },
});

const checkoutIndexRoute = createRoute({
  getParentRoute: () => checkoutLayoutRoute,
  path: "/",
  component: function CheckoutIndexRoute() {
    const { dispensarySlug } = checkoutIndexRoute.useParams();
    return <CheckoutScreen dispensarySlug={dispensarySlug} />;
  },
});

/**
 * Back-compat redirect for `/checkout/review` — the page collapsed into the
 * single-page checkout (PR 1.5). Bookmarks and in-flight links continue to
 * resolve to the new screen.
 */
const checkoutReviewRoute = createRoute({
  getParentRoute: () => checkoutLayoutRoute,
  path: "review",
  beforeLoad: ({ params }) => {
    throw redirect({
      to: "/$dispensarySlug/checkout",
      params: { dispensarySlug: params.dispensarySlug },
    });
  },
  component: function CheckoutReviewRedirectStub() {
    return null;
  },
});

const checkoutTree = checkoutLayoutRoute.addChildren([
  checkoutIndexRoute,
  checkoutReviewRoute,
]);

const dispensaryOrderRoute = createRoute({
  getParentRoute: () => dispensaryShellRoute,
  path: "orders/$orderId",
  component: function OrderRoute() {
    const { dispensarySlug, orderId } = dispensaryOrderRoute.useParams();
    return <OrderTrackingScreen dispensarySlug={dispensarySlug} orderId={orderId} />;
  },
});

const dispensaryShopTree = dispensaryShellRoute.addChildren([
  dispensaryMenuRoute,
  dispensaryCartRoute,
  checkoutTree,
  dispensaryOrderRoute,
]);

const accountLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "account",
  component: function AccountLayout() {
    return (
      <>
        <AccountSubNav />
        <Outlet />
      </>
    );
  },
});

const accountIndexRoute = createRoute({
  getParentRoute: () => accountLayoutRoute,
  path: "/",
  component: AccountLandingScreen,
});

const accountOrdersRedirectRoute = createRoute({
  getParentRoute: () => accountLayoutRoute,
  path: "orders",
  beforeLoad: () => {
    throw redirect({ to: "/orders" });
  },
  component: function AccountOrdersRedirectStub() {
    return null;
  },
});

const accountAddressesRoute = createRoute({
  getParentRoute: () => accountLayoutRoute,
  path: "addresses",
  component: function AccountAddresses() {
    return <AccountAddressesScreen />;
  },
});

const accountPaymentRoute = createRoute({
  getParentRoute: () => accountLayoutRoute,
  path: "payment",
  component: function AccountPayment() {
    return <AccountPaymentScreen />;
  },
});

const accountIdentityRoute = createRoute({
  getParentRoute: () => accountLayoutRoute,
  path: "identity",
  component: function AccountIdentity() {
    return <AccountIdentityScreen />;
  },
});

const accountSupportRoute = createRoute({
  getParentRoute: () => accountLayoutRoute,
  path: "support",
  component: function AccountSupport() {
    return <AccountSupportScreen />;
  },
});

const accountTree = accountLayoutRoute.addChildren([
  accountIndexRoute,
  accountOrdersRedirectRoute,
  accountAddressesRoute,
  accountPaymentRoute,
  accountIdentityRoute,
  accountSupportRoute,
]);

const routeTree = rootRoute.addChildren([
  indexRoute,
  signInTree,
  signUpTree,
  ordersRoute,
  accountTree,
  dispensaryShopTree,
]);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
